import { api } from '@/api';
import type {
  AuditEntry,
  Consent,
  Course,
  Group,
  GroupDetail,
  ImportResult,
  Institution,
  Me,
  ModuleInfo,
  Overview,
  Page,
  Person,
  Role,
  SignupOptions,
  SignupRole,
} from './types';

export const getMe = (signal?: AbortSignal) => api.get<Me>('/me', { signal });
export const updateProfile = (name: string) => api.patch<Person>('/me', { name });
export interface ProfileInput {
  name: string;
  role: SignupRole;
  /** Students: exactly one. Teachers: every institution they teach at. */
  institutionIds: string[];
  courseId: string | null;
  institutionalEmail: string;
  consent: Consent;
}
/**
 * Saves name, institution(s) and course, then e-mails a confirmation link to the institutional
 * address. `done` when that address was already confirmed (e.g. signed in with it on Google).
 */
export const completeProfile = (input: ProfileInput) =>
  api.post<{ status: 'sent' | 'done' }>('/me/institutional-email', input);
/** Someone who studies on their own (e.g. for the ENEM): just the name, no institution. */
export const completeIndependentProfile = (input: { name: string; consent: Consent }) =>
  api.post<Person>('/me/independent', input);
/** Test accounts: enter as a student (institution + course), a teacher or a coordinator. */
export const setTestProfile = (input: {
  role: SignupRole | 'coordinator';
  institutionId: string;
  courseId: string | null;
}) => api.post<Person>('/me/test-profile', input);
/** Changes what the person agreed to receive, at any time. */
export const updateConsent = (consent: Consent) => api.patch<Consent>('/me/consent', consent);
export const getSignupOptions = (signal?: AbortSignal) =>
  api.get<SignupOptions>('/signup-options', { signal });
/** Opened from the e-mailed link; works without a session. */
export const verifyInstitutionalEmail = (token: string) =>
  api.post<{ email: string }>('/auth/verify-email', { token }, { auth: false });

// ── Approvals (teachers who confirmed @cps.sp.gov.br) ──────────────────────
export const listApprovals = (signal?: AbortSignal) =>
  api.get<Person[]>('/admin/approvals', { signal });
export const approveRequest = (userId: string) =>
  api.post<Person>(`/admin/approvals/${userId}/approve`);
export const rejectRequest = (userId: string) =>
  api.post<Person>(`/admin/approvals/${userId}/reject`);

// ── Institutions and courses ────────────────────────────────────────────────
export const listCourses = (includeInactive = false, signal?: AbortSignal) =>
  api.get<Course[]>('/courses', { query: { includeInactive }, signal });
export const listInstitutions = (signal?: AbortSignal) =>
  api.get<Institution[]>('/admin/institutions', { signal });

export interface InstitutionInput {
  name: string;
  emailRules: Institution['emailRules'];
  allowedEmails: Institution['allowedEmails'];
  isActive: boolean;
  /** On creation: free access (courtesy) right away. */
  courtesy?: boolean;
}
export const createInstitution = (input: InstitutionInput) =>
  api.post<Institution>('/admin/institutions', input);
export const updateInstitution = (id: string, input: InstitutionInput) =>
  api.patch<Institution>(`/admin/institutions/${id}`, input);
export const createCourse = (input: { name: string; isActive: boolean }) =>
  api.post<Course>('/admin/courses', input);
export const updateCourse = (id: string, input: { name: string; isActive: boolean }) =>
  api.patch<Course>(`/admin/courses/${id}`, input);

// ── Modules ─────────────────────────────────────────────────────────────────
export const toggleModule = (key: string, enabled: boolean) =>
  api.patch<ModuleInfo>(`/admin/modules/${key}`, { enabled });
export const getOverview = (signal?: AbortSignal) =>
  api.get<Overview>('/admin/overview', { signal });
export const getAudit = (limit = 50, signal?: AbortSignal) =>
  api.get<AuditEntry[]>('/admin/audit', { query: { limit }, signal });

// ── People ──────────────────────────────────────────────────────────────────
export type UserSort = 'name' | 'role' | 'groups' | 'lastLogin';

export interface UserQuery {
  search?: string;
  role?: Role | '';
  groupId?: string;
  courseId?: string;
  status?: 'active' | 'inactive' | '';
  sort?: UserSort;
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export const listUsers = ({ role, status, ...query }: UserQuery = {}, signal?: AbortSignal) =>
  api.get<Page<Person>>('/users', {
    query: { ...query, role: role || undefined, status: status || undefined },
    signal,
  });

export interface UserInput {
  email: string;
  name: string;
  role: Role;
  registration: string | null;
  password?: string | null;
  groupIds: string[];
  /** Empty on creation: the institution chosen in the top bar. */
  institutionIds?: string[];
  courseId?: string | null;
  phone?: string | null;
  personalEmail?: string | null;
  isActive?: boolean;
}

export const createUser = (input: UserInput) => api.post<Person>('/users', input);
export const updateUser = (id: string, input: Partial<UserInput>) =>
  api.patch<Person>(`/users/${id}`, input);
export const setUserPassword = (id: string, password: string) =>
  api.post<void>(`/users/${id}/password`, { password });
export const deleteUser = (id: string) => api.delete<void>(`/users/${id}`);

// ── Groups ──────────────────────────────────────────────────────────────────
export const listGroups = (includeArchived = false, signal?: AbortSignal) =>
  api.get<Group[]>('/groups', { query: { includeArchived }, signal });
export const getGroup = (id: string, signal?: AbortSignal) =>
  api.get<GroupDetail>(`/groups/${id}`, { signal });

export interface GroupInput {
  name: string;
  code: string | null;
  term: string | null;
  description: string | null;
  archived: boolean;
}

export const createGroup = (input: GroupInput) => api.post<Group>('/groups', input);
export const updateGroup = (id: string, input: GroupInput) =>
  api.patch<Group>(`/groups/${id}`, input);
export const deleteGroup = (id: string) => api.delete<void>(`/groups/${id}`);
export const addGroupMembers = (id: string, userIds: string[]) =>
  api.post<Group>(`/groups/${id}/members`, { userIds });
export const removeGroupMember = (id: string, userId: string) =>
  api.delete<Group>(`/groups/${id}/members/${userId}`);

// ── CSV import / download ───────────────────────────────────────────────────
export function importCsv(path: string, file: File, dryRun: boolean) {
  const form = new FormData();
  form.append('file', file);
  return api.post<ImportResult>(path, form, { query: { dryRun }, timeoutMs: 60_000 });
}

/** Downloads an authenticated file (CSV or Excel template, report export) and saves it. */
export async function downloadFile(path: string, filename: string) {
  const content = await api.get<string | Blob>(path);
  const blob =
    content instanceof Blob ? content : new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
